/**
 * `/stats` — TASK 10.2 Step 1.
 *
 * The mock's YOUR NUMBERS screen (mock/SteamyMusic.dc.html L537-573): the
 * ALL TIME / LAST 30 DAYS period toggle above two ranked columns — Top Songs
 * (≤ 25 button rows that hand the tapped entry to the composition root) and
 * Top Albums (≤ 25 anchor rows into `#/albums/:id`) — wired to
 * `GET /api/stats` (TASK 4.4) instead of `db.topSongs`/`db.topAlbums`.
 *
 * ── MOCK-DEFECT FIX 11: DISCRETE ARIA-LABELS ──────────────────────────────
 * The mock's row accessible name was the CONCATENATED visible text
 * ("1 15 StepRadiohead — In Rainbows 210 pla…" — title and artist run
 * together, then truncated), which a screen reader announces as gibberish and
 * which made the derived testids fragile. Every row here carries an explicit
 * `aria-label` built by {@link statsSongAriaLabel} / {@link statsAlbumAriaLabel}
 * from the DISCRETE fields — rank, title, artist, album, play count — each
 * separated by real punctuation, never truncated. The visible spans are
 * untouched (the ui-contract §5 baseline keeps the mock's layout); only the
 * accessible name is fixed.
 *
 * ── PERIOD TOGGLE (AC 12.2) ───────────────────────────────────────────────
 * `all_time` ↔ `last_30_days` (a ROLLING 30×24 h UTC window — dto.ts
 * `statsPeriodSchema`). The toggle repaints `aria-pressed` synchronously and
 * reloads; a slower, older period's response can never overwrite a newer
 * one's paint (generation guard + abort, the sibling views' idiom).
 *
 * ── STATES (AC 12.5 / 12.6) ───────────────────────────────────────────────
 * loading — one announced skeleton replacing the columns;
 * error   — the shared retriable ErrorBlock (Retry re-runs the same period);
 * empty   — BOTH lists empty is the genuine "no qualifying plays yet" state
 *           (never an error — dto.ts pins empty arrays as valid); ONE empty
 *           list renders as an inline per-column strip beside the served one.
 *
 * @module
 */

/* global AbortController -- browser platform API; see FavoritesView.js */

import { TESTIDS, albumCardLink, topSongRowButton } from '@streamymusic/shared';

import { EmptyBlock, ErrorBlock, LoadingBlock } from '../../components/StateBlocks.js';
import { Tab } from '../../components/primitives/Chip.js';
import { append, el } from '../../components/primitives/dom.js';
import { albumThumb } from '../../components/CoverThumb.js';

/** §10.6 — the whole-page empty state before any qualifying play (AC 12.5). */
export const STATS_EMPTY_MESSAGE =
  'No qualifying plays yet — press play and your numbers start counting.';
/** Per-column inline strip when only ONE list is empty for the period. */
export const STATS_TOP_SONGS_EMPTY_MESSAGE = 'No qualifying song plays in this period yet.';
export const STATS_TOP_ALBUMS_EMPTY_MESSAGE = 'No qualifying album plays in this period yet.';

/**
 * "210 plays" / "1 play" — the one place the count is pluralised, shared by
 * the visible span and both aria-label builders so they can never disagree.
 *
 * @param {number} playCount
 * @returns {string}
 */
export function playsLabel(playCount) {
  return `${playCount.toLocaleString()} ${playCount === 1 ? 'play' : 'plays'}`;
}

/**
 * Mock-defect fix 11 — the DISCRETE Top-Songs row accessible name. Every §12
 * field the row communicates, as readable prose, never concatenated or
 * truncated: rank, title, artist, album, play count.
 *
 * @param {import('@streamymusic/shared').StatsSongEntry} entry
 * @returns {string}
 */
export function statsSongAriaLabel(entry) {
  return (
    `Rank ${entry.rank}: ${entry.track.title} by ${entry.track.artistName}, ` +
    `from ${entry.track.albumTitle} — ${playsLabel(entry.playCount)}`
  );
}

/**
 * Mock-defect fix 11 — the DISCRETE Top-Albums row accessible name.
 *
 * @param {import('@streamymusic/shared').StatsAlbumEntry} entry
 * @returns {string}
 */
export function statsAlbumAriaLabel(entry) {
  return (
    `Rank ${entry.rank}: ${entry.album.title} by ${entry.album.artistName} — ` +
    `${playsLabel(entry.playCount)}`
  );
}

/**
 * The mock's rank accent (`noCol`, L1101): 1 pink, 2 yellow, 3 teal, the
 * rest dim. Data-driven per row, so applied through CSSOM like every other
 * per-item accent (see primitives/Pill.js) — never a style string. The dim
 * tier is the AA-fixed `--sm-color-text-dim-2`, not the mock's failing
 * #66597e (styles/components.css header).
 *
 * @param {number} rank
 * @returns {string} a CSS color value
 */
export function rankColor(rank) {
  if (rank === 1) return 'var(--sm-color-pink)';
  if (rank === 2) return 'var(--sm-color-yellow)';
  if (rank === 3) return 'var(--sm-color-teal)';
  return 'var(--sm-color-text-dim-2)';
}

/**
 * The api surface this view needs. `getStats` is being added to the shared
 * ApiClient by the composition task; this structural type is what the view
 * codes against.
 *
 * @typedef {object} StatsApi
 * @property {(options?: {
 *   period?: import('@streamymusic/shared').StatsPeriod,
 *   signal?: AbortSignal,
 * }) => Promise<import('@streamymusic/shared').StatsResponse>} getStats
 */

/**
 * @typedef {object} StatsHandlers
 * @property {(entry: import('@streamymusic/shared').StatsSongEntry) => void} onSongRow
 *   a Top-Songs row tap — the composition root routes it into the track menu
 *   (ui-contract §3: the ranked rows "open the track menu")
 */

/**
 * Builds the `/stats` section and starts loading the initial period.
 *
 * @param {{
 *   api: StatsApi,
 *   heading: HTMLElement,
 *   handlers: StatsHandlers,
 *   initialPeriod?: import('@streamymusic/shared').StatsPeriod,
 *   onPeriodChange?: (period: import('@streamymusic/shared').StatsPeriod) => void,
 *   announce?: (text: string) => void,
 * }} options
 *   `heading` is the route's `<h1 data-testid="page-heading">` — the shell
 *   contract requires exactly one per route, so the view is handed it.
 *   `onPeriodChange` lets the composition root record the chosen period in the
 *   URL (so a later Back restores the same view), WITHOUT this view importing
 *   the router or forcing a re-mount — the toggle keeps its in-place
 *   `aria-pressed` update. `initialPeriod` seeds it from that URL on return.
 * @returns {{ element: HTMLElement, dispose: () => void }}
 */
export function StatsView(options) {
  const { api, heading, handlers, initialPeriod = 'all_time', onPeriodChange, announce } = options;

  const section = el('section', { attrs: { 'data-screen-label': 'Stats' } });

  /** @type {import('@streamymusic/shared').StatsPeriod} */
  let period = initialPeriod;

  /* ── period toggle (mock L540-543; buttons + aria-pressed, not tabs) ──── */

  const allTimeButton = Tab({
    label: 'ALL TIME',
    accent: 'teal',
    size: 'period',
    selected: period === 'all_time',
    testId: TESTIDS.ALL_TIME_BUTTON,
    onClick: () => setPeriod('all_time'),
  });
  const last30Button = Tab({
    label: 'LAST 30 DAYS',
    accent: 'yellow',
    size: 'period',
    selected: period === 'last_30_days',
    testId: TESTIDS.LAST_30_DAYS_BUTTON,
    onClick: () => setPeriod('last_30_days'),
  });

  const toggleRow = el('div', {
    className: 'sm-tab-row',
    attrs: { role: 'group', 'aria-label': 'Stats period' },
  });
  append(toggleRow, allTimeButton, last30Button);

  /** The columns / skeleton / error / empty all swap through this body. */
  const body = el('div');

  append(section, heading, toggleRow, body);

  /* ── lifecycle guards (sibling idiom — FavoritesView.js) ──────────────── */

  let disposed = false;
  let generation = 0;
  /** @type {AbortController|null} */
  let controller = null;

  /** @param {import('@streamymusic/shared').StatsPeriod} next */
  function setPeriod(next) {
    // Re-pressing the active period is a no-op — no refetch, no repaint.
    if (next === period) return;
    period = next;
    paintToggle();
    // Record the choice in the URL so Back returns to THIS period, not the
    // default. A no-op in isolation (direct-mounted tests pass nothing); the
    // composition root wires it to a `replaceState`, so the toggle stays an
    // in-place update and never spams history.
    onPeriodChange?.(next);
    void load();
  }

  function paintToggle() {
    // aria-pressed IS the visual state: the `.sm-tab--period-*[aria-pressed]`
    // selectors paint the fill from the a11y attribute (primitives/Chip.js),
    // so state is never conveyed by colour alone.
    allTimeButton.setAttribute('aria-pressed', String(period === 'all_time'));
    last30Button.setAttribute('aria-pressed', String(period === 'last_30_days'));
  }

  /* ── rows ─────────────────────────────────────────────────────────────── */

  /**
   * One ranked Top-Songs BUTTON row (mock L549-556). The accessible name is
   * the discrete label; the visible spans keep the mock's layout.
   *
   * @param {import('@streamymusic/shared').StatsSongEntry} entry
   * @returns {HTMLElement}
   */
  function SongRow(entry) {
    const track = entry.track;
    const row = el('button', {
      className: 'sm-row sm-row--button sm-row--gap10',
      testId: topSongRowButton(
        entry.rank,
        `${track.title} ${track.artistName} ${track.albumTitle}`,
        entry.playCount,
      ),
      attrs: { type: 'button', 'aria-label': statsSongAriaLabel(entry) },
      on: { click: () => handlers.onSongRow(entry) },
    });

    const textCell = el('span', { className: 'sm-flex-fill' });
    append(
      textCell,
      el('span', { className: 'sm-line-primary', text: track.title }),
      el('span', {
        className: 'sm-line-secondary',
        text: `${track.artistName} — ${track.albumTitle}`,
      }),
    );

    append(
      row,
      el('span', {
        className: 'sm-rank',
        text: String(entry.rank),
        styleProps: { color: rankColor(entry.rank) },
      }),
      albumThumb(
        { id: track.albumId, title: track.albumTitle, coverRef: track.coverRef },
        'sm-thumb sm-thumb--36',
      ),
      textCell,
      el('span', { className: 'sm-plays', text: playsLabel(entry.playCount) }),
    );
    return row;
  }

  /**
   * One ranked Top-Albums ANCHOR row (mock L561-569) into `#/albums/:id`.
   *
   * @param {import('@streamymusic/shared').StatsAlbumEntry} entry
   * @returns {HTMLElement}
   */
  function AlbumRow(entry) {
    const album = entry.album;
    const row = el('a', {
      className: 'sm-row sm-row--button sm-row--gap10',
      testId: albumCardLink(album.id),
      attrs: {
        href: `#/albums/${encodeURIComponent(album.id)}`,
        'aria-label': statsAlbumAriaLabel(entry),
      },
    });

    const textCell = el('span', { className: 'sm-flex-fill' });
    append(
      textCell,
      el('span', { className: 'sm-line-primary', text: album.title }),
      el('span', { className: 'sm-line-secondary', text: album.artistName }),
    );

    append(
      row,
      el('span', {
        className: 'sm-rank',
        text: String(entry.rank),
        styleProps: { color: rankColor(entry.rank) },
      }),
      albumThumb(album, 'sm-thumb sm-thumb--36'),
      textCell,
      el('span', { className: 'sm-plays', text: playsLabel(entry.playCount) }),
    );
    return row;
  }

  /* ── paints ───────────────────────────────────────────────────────────── */

  /**
   * The mock's per-column inline strip for a one-sided empty period —
   * deliberately NOT the whole-page EmptyBlock, which is reserved for the
   * genuine both-lists-empty state (see the module header).
   *
   * @param {string} message
   * @returns {HTMLElement}
   */
  function ColumnEmpty(message) {
    return el('div', {
      className: 'sm-empty sm-empty--inline sm-empty--inline-18',
      text: message,
      attrs: { role: 'status' },
    });
  }

  /** @param {import('@streamymusic/shared').StatsResponse} stats */
  function paint(stats) {
    if (stats.topSongs.length === 0 && stats.topAlbums.length === 0) {
      // The AC 12.5 empty state — a valid answer, never an error.
      body.replaceChildren(
        EmptyBlock({
          message: STATS_EMPTY_MESSAGE,
          ...(announce === undefined ? {} : { announce }),
        }),
      );
      return;
    }

    const songsColumn = el('div');
    append(
      songsColumn,
      el('h2', {
        className: [
          'sm-section-title',
          'sm-section-title--22',
          'sm-section-title--pink',
          'sm-section-title--mb10',
        ],
        text: 'TOP SONGS',
      }),
    );
    if (stats.topSongs.length === 0) {
      append(songsColumn, ColumnEmpty(STATS_TOP_SONGS_EMPTY_MESSAGE));
    } else {
      const list = el('div', { className: 'sm-stack-6' });
      for (const entry of stats.topSongs) append(list, SongRow(entry));
      append(songsColumn, list);
    }

    const albumsColumn = el('div');
    append(
      albumsColumn,
      el('h2', {
        className: [
          'sm-section-title',
          'sm-section-title--22',
          'sm-section-title--yellow',
          'sm-section-title--mb10',
        ],
        text: 'TOP ALBUMS',
      }),
    );
    if (stats.topAlbums.length === 0) {
      append(albumsColumn, ColumnEmpty(STATS_TOP_ALBUMS_EMPTY_MESSAGE));
    } else {
      const list = el('div', { className: 'sm-stack-6' });
      for (const entry of stats.topAlbums) append(list, AlbumRow(entry));
      append(albumsColumn, list);
    }

    const grid = el('div', { className: 'sm-grid-stats' });
    // Feature request #6: albums lead, then songs.
    append(grid, albumsColumn, songsColumn);
    body.replaceChildren(grid);
  }

  /* ── load ─────────────────────────────────────────────────────────────── */

  async function load() {
    const own = (generation += 1);
    controller?.abort();
    const ownController = new AbortController();
    controller = ownController;

    body.replaceChildren(LoadingBlock({ count: 6, label: 'Loading your stats' }));

    try {
      const stats = await api.getStats({ period, signal: ownController.signal });
      if (disposed || own !== generation) return;
      paint(stats);
    } catch {
      if (disposed || own !== generation) return;
      body.replaceChildren(
        ErrorBlock({
          onRetry: () => {
            void load();
          },
          ...(announce === undefined ? {} : { announce }),
        }),
      );
    }
  }

  void load();

  return {
    element: section,
    dispose() {
      disposed = true;
      controller?.abort();
    },
  };
}
